import { createContext } from "react";

export interface ObjectEditorItemOptions {
  name: string;
  label?: string;
  type: "select" | "string" | "boolean" | "number";
  options?: { label: string; value: string | number | boolean | undefined }[];
  dataPath?: string;
  customRenderer?: (
    value: string | string[] | number | number[] | boolean | undefined | null,
  ) => React.ReactElement;
}

interface ObjectEditorContextProps<T extends Record<string, unknown>> {
  object: T;
  setObject: React.Dispatch<React.SetStateAction<T>>;
  objectTemplate: ObjectEditorItemOptions[];
}

export const ObjectEditorContext = createContext<ObjectEditorContextProps<
  Record<string, unknown>
> | null>(null);

interface ObjectEditorContextProviderProps<
  T extends Record<string, unknown>,
> extends ObjectEditorContextProps<T> {
  children: React.ReactNode;
}

/**
 * Provider component that supplies ObjectEditor state (the object being
 * edited), a setter, and the template describing editable fields to all
 * nested object editor components.
 *
 * @param props - Component props including the object, setter, template and children.
 * @param props.object - The object instance being edited.
 * @param props.setObject - Setter function to update the object state.
 * @param props.objectTemplate - Template describing editable fields.
 * @param props.children - Child nodes that will consume the context.
 */
export const ObjectEditorContextProvider = <T extends Record<string, unknown>>({
  object,
  setObject,
  objectTemplate,
  children,
}: ObjectEditorContextProviderProps<T>) => {
  return (
    <ObjectEditorContext.Provider
      value={{
        object,
        setObject: setObject as React.Dispatch<React.SetStateAction<Record<string, unknown>>>,
        objectTemplate,
      }}
    >
      {children}
    </ObjectEditorContext.Provider>
  );
};

ObjectEditorContextProvider.displayName = "ObjectEditorContextProvider";
